import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import TeamSection from "./components/TeamSection";
import ValuesSection from "./components/ValuesSection";
import StatsSection from "./components/StatsSection";
import PartnersSection from "./components/PartnersSection";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

export default function AProposPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

  useSeoMeta({
    title: "À propos de Wazi Stories Afrique — Notre mission pour les entrepreneurs africains",
    description: "Wazi Stories Afrique valorise les entrepreneurs africains à travers des success stories, interviews et formations. Découvrez notre mission, notre équipe et nos valeurs.",
    type: "website",
    keywords: "à propos Wazi Stories, mission entrepreneur africain, équipe Wazi, média africain entrepreneuriat",
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "url": `${siteUrl}/a-propos`,
    "name": "À propos de Wazi Stories Afrique",
    "description": "Wazi Stories Afrique valorise les entrepreneurs africains à travers des success stories, interviews et formations.",
    "publisher": {
      "@type": "Organization",
      "name": "Wazi Stories Afrique",
      "url": siteUrl,
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "contact@waziconsulting.com",
        "contactType": "customer support"
      }
    }
  }, "apropos");

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-16 md:pt-20 overflow-hidden">
        <div className="relative h-[55vw] min-h-[260px] max-h-[560px]">
          <img
            src="https://public.readdy.ai/ai/img_res/b9c2f74708c66c6981eacc690e2eb91e.jpg"
            alt={t("apropos.hero.headline")}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 bg-orange-500/90 text-white text-xs font-bold px-3 md:px-4 py-2 rounded-full mb-3 md:mb-5 uppercase tracking-wider">
                  <i className="ri-heart-line text-yellow-300"></i>
                  {t("apropos.hero.badge")}
                </div>
                <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold text-white font-serif leading-tight mb-3 md:mb-5">
                  {t("apropos.hero.headline")}
                </h1>
                <p className="text-white/80 text-sm md:text-lg leading-relaxed max-w-xl hidden sm:block">
                  {t("apropos.hero.subtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <div className="w-10 h-1 rounded-full bg-orange-500 mb-3 md:mb-4"></div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 font-serif mb-4 md:mb-6">
                {t("apropos.mission.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4 md:mb-6">
                {t("apropos.mission.p1")}
              </p>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-6 md:mb-8">
                {t("apropos.mission.p2")}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/success-stories"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 md:px-6 py-2.5 md:py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-trophy-line"></i>
                  {t("apropos.mission.cta.stories")}
                </Link>
                <Link
                  to="/soumettre"
                  className="inline-flex items-center gap-2 border border-orange-500 text-orange-500 hover:bg-orange-50 font-semibold px-5 md:px-6 py-2.5 md:py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-add-circle-line"></i>
                  {t("apropos.mission.cta.submit")}
                </Link>
              </div>
            </div>

            {/* Image + floating badges */}
            <div className="relative mt-6 lg:mt-0">
              <div className="rounded-2xl overflow-hidden" style={{ height: "320px" }}>
                <img
                  src="https://readdy.ai/api/search-image?query=african%20entrepreneur%20woman%20man%20team%20meeting%20startup%20office%20Abidjan%20modern%20workspace%20laptop%20discussion%20planning%20positive%20energy&width=700&height=500&seq=mission-img&orientation=portrait"
                  alt={t("apropos.mission.title")}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="hidden sm:block absolute -bottom-4 -left-4 bg-orange-500 text-white rounded-2xl p-4 md:p-5">
                <div className="text-2xl md:text-3xl font-bold">500+</div>
                <div className="text-xs md:text-sm font-medium text-orange-100">{t("apropos.mission.badge.stories")}</div>
              </div>
              <div className="hidden sm:block absolute -top-4 -right-4 bg-white rounded-2xl p-4 md:p-5 border border-gray-100">
                <div className="text-2xl md:text-3xl font-bold text-gray-900">54</div>
                <div className="text-xs md:text-sm font-medium text-gray-500">{t("apropos.mission.badge.countries")}</div>
              </div>
            </div>
          </div>

          {/* Stats chips on mobile */}
          <div className="flex gap-4 mt-6 sm:hidden">
            <div className="flex-1 bg-orange-500 text-white rounded-2xl p-4 text-center">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-xs font-medium text-orange-100">{t("apropos.mission.badge.stories")}</div>
            </div>
            <div className="flex-1 bg-white rounded-2xl p-4 text-center border border-gray-100">
              <div className="text-2xl font-bold text-gray-900">54</div>
              <div className="text-xs font-medium text-gray-500">{t("apropos.mission.badge.countries")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <StatsSection />

      {/* ── Values ── */}
      <ValuesSection />

      {/* ── Team ── */}
      <TeamSection />

      {/* ── Partners ── */}
      <PartnersSection />

      {/* ── CTA Final ── */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20african%20geometric%20pattern%20colorful%20texture%20art%20background%20warm&width=1400&height=400&seq=cta-apropos&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white font-serif mb-3 md:mb-4">
            {t("apropos.cta.title")}
          </h2>
          <p className="text-white/85 text-sm md:text-lg mb-6 md:mb-8 leading-relaxed">
            {t("apropos.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link
              to="/soumettre"
              className="inline-flex items-center justify-center gap-2 bg-white text-orange-500 font-bold px-6 md:px-8 py-3 md:py-4 rounded-full hover:bg-orange-50 transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap text-sm md:text-base"
            >
              <i className="ri-pencil-line text-base md:text-lg"></i>
              {t("apropos.cta.submit")}
            </Link>
            <Link
              to="/#newsletter"
              className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm border border-white/40 text-white font-semibold px-6 md:px-8 py-3 md:py-4 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base"
            >
              <i className="ri-mail-line text-base md:text-lg"></i>
              {t("apropos.cta.newsletter")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
